import { SESSION_MAX_AGE_SECONDS } from "@contract";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useSignIn } from "../api/hooks";
import { GateButton } from "../components/control/GateButton";
import { TextInput } from "../components/control/TextInput";
import { ErrorBanner } from "../components/feedback/ErrorBanner";
import { useGuidance } from "../components/guidance";
import { LanguageSwitcher } from "../components/layout/LanguageSwitcher";
import { ThemeSwitcher } from "../components/layout/ThemeSwitcher";
import { Wordmark } from "../components/layout/Wordmark";

const SESSION_DAYS = Math.round(SESSION_MAX_AGE_SECONDS / 86_400);

/**
 * Where to land after a successful sign-in. Only a path inside the app is
 * followed: `state.from` reaches this screen from the router, and a crafted
 * "//evil.example" would otherwise be a protocol-relative URL that bounces the
 * Author off the service the moment they get in.
 */
export function redirectTarget(state: unknown): string {
  const asked = (state as { from?: unknown } | null)?.from;
  if (typeof asked !== "string") return "/";
  return asked.startsWith("/") && !asked.startsWith("//") ? asked : "/";
}

/**
 * The gate: one card on the ground colour with the wordmark, one password
 * field, and the DE|EN switcher, so the Author can read the gate in German
 * before they are inside. The form submits on Enter and on the button, and a
 * refusal is rendered from the API's own error code (INVALID_PASSWORD,
 * TOO_MANY_ATTEMPTS), never from a message the server invented.
 */
export function SignIn() {
  const { t } = useTranslation("signin");
  const [password, setPassword] = useState("");
  const signIn = useSignIn();
  const navigate = useNavigate();
  const location = useLocation();
  const field = useRef<HTMLInputElement>(null);
  // autoFocus alone loses the race with the router's first paint on a deep
  // link, so the focus is set once the field is in the document.
  useEffect(() => field.current?.focus(), []);
  // The empty-password refusal is said, not greyed: the field highlights and
  // takes focus back, which also recovers it after a paste that never landed.
  const guide = useGuidance();

  // Where RequireAuth turned the Author away from; "/" when they came here themselves.
  const from = redirectTarget(location.state);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (password.length === 0 || signIn.isPending) return;
    signIn.mutate(password, { onSuccess: () => navigate(from, { replace: true }) });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-ground p-6">
      <div className="w-full max-w-sm rounded-md border border-line bg-surface p-6">
        <div className="mb-5 flex items-center justify-between gap-4">
          <Wordmark />
          <LanguageSwitcher />
        </div>
        <h1 className="mb-1 font-serif text-title font-semibold">{t("title")}</h1>
        <p className="mb-4 text-ink-2">{t("intro")}</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <TextInput
            ref={field}
            label={t("passwordLabel")}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            placeholder={t("passwordPlaceholder")}
            onChange={(e) => setPassword(e.target.value)}
            hint={t("staySignedIn", { count: SESSION_DAYS })}
            error={guide.attempted && password.length === 0 ? t("passwordRequired") : undefined}
          />
          {signIn.isError ? <ErrorBanner error={signIn.error} /> : null}
          <GateButton
            type="submit"
            busy={signIn.isPending}
            gate={{ blocked: password.length === 0, reason: t("passwordRequired") }}
            onBlocked={guide.proceed}
          >
            {signIn.isPending ? t("submitting") : t("submit")}
          </GateButton>
        </form>
        <div className="mt-5 flex justify-end">
          <ThemeSwitcher />
        </div>
      </div>
    </main>
  );
}
